"use client";

import React from 'react';

export interface LayoutBlueprintBlockProps {
    subtitle?: string;
    title?: string;
    description?: string;
    
    // Left Box
    leftTag?: string;
    leftTitle?: string;
    leftDesc?: string;
    leftFooter?: string;
    
    // Center Box
    centerTag?: string;
    centerText?: string;
    centerLabel?: string;
    centerValue?: string;
    centerFooter?: string;
    
    // Right Box
    rightTag?: string;
    rightLabel1?: string;
    rightValue1?: string;
    rightLabel2?: string;
    rightValue2?: string;
    rightButtonText?: string;
    
    // Bottom Details
    systemLabel?: string;
    details?: { label: string; value: string }[];
    
    style?: {
        backgroundColor?: string;
        textColor?: string;
        accentColor?: string;
        paddingTop?: string;
        paddingBottom?: string;
    };
}

export default function LayoutBlueprintBlock({
    subtitle = "02 / VISUAL SPECIFICATION",
    title = "Layout System & Grid Architecture",
    description = "A showcase of the pixel-perfect alignment blueprint. Every component lies inside a structured vector system to preserve mathematical balance across viewports.",
    leftTag = "Grid Col (W: 305px)",
    leftTitle = "A Study in Spacing",
    leftDesc = "We mapped the layout using a strict geometric system, balancing imagery with editorial text blocks.",
    leftFooter = "Padding: 24px | Gap: 24px",
    centerTag = "Grid Col (W: 410px)",
    centerText = "IMAGE CONTAINER",
    centerLabel = "Aspect Ratio: 16:9",
    centerValue = "Fit Cover",
    centerFooter = "Borders: 1px Solid Zinc-850",
    rightTag = "Grid Col (W: 220px)",
    rightLabel1 = "STARTING RATE",
    rightValue1 = "950 SR",
    rightLabel2 = "ROOM SIZE",
    rightValue2 = "30 SQM",
    rightButtonText = "BOOK NOW",
    systemLabel = "DESKTOP GRID SYSTEM: 12-COLUMN ASYMMETRIC / 1440PX WIDTH",
    details = [
        { label: "COLUMNS", value: "12" },
        { label: "GUTTER", value: "24PX" },
        { label: "MARGIN", value: "80PX" }
    ],
    style = {}
}: LayoutBlueprintBlockProps) {
    const bg = style.backgroundColor || "#0B0B0C";
    const text = style.textColor || "#FAF7F2";
    const accent = style.accentColor || "#c5a880";

    return (
        <div 
            className="w-full px-6 md:px-12 lg:px-24"
            style={{
                backgroundColor: bg,
                color: text,
                paddingTop: style.paddingTop || "7rem",
                paddingBottom: style.paddingBottom || "7rem",
            }}
        >
            <div className="container mx-auto">
                <div className="max-w-3xl mb-16">
                    {subtitle && (
                        <span 
                            className="text-xs font-mono tracking-widest block mb-2"
                            style={{ color: accent }}
                        >
                            {subtitle}
                        </span>
                    )}
                    {title && (
                        <h2 className="text-4xl md:text-5xl font-light leading-tight font-cormorant">
                            {title}
                        </h2>
                    )}
                    {description && (
                        <p className="text-zinc-400 text-sm mt-3 font-light max-w-xl leading-relaxed">
                            {description}
                        </p>
                    )}
                </div>

                {/* The Grid Blueprint Card */}
                <div className="w-full bg-[#0E0E10] border border-zinc-850 rounded-2xl p-6 md:p-8 lg:p-12 relative overflow-hidden">
                    
                    {/* Figma Grid Ruler Markings (Top & Left) */}
                    <div className="absolute top-0 left-12 right-12 h-6 border-b border-zinc-900 flex justify-between items-center text-[9px] font-mono text-zinc-600 select-none pointer-events-none">
                        <span>0px</span>
                        <span>305px</span>
                        <span>626px</span>
                        <span>947px</span>
                        <span>1268px</span>
                        <span>1440px</span>
                    </div>
                    <div className="absolute left-0 top-12 bottom-12 w-6 border-r border-zinc-900 flex flex-col justify-between items-center text-[9px] font-mono text-zinc-650 py-2 select-none pointer-events-none">
                        <span>Y:0</span>
                        <span>Y:200</span>
                        <span>Y:400</span>
                        <span>Y:600</span>
                    </div>

                    {/* Interactive Blueprint Mockup */}
                    <div className="pt-8 grid grid-cols-1 md:grid-cols-12 gap-6 relative z-10">
                        
                        {/* Left block representation */}
                        <div className="md:col-span-4 border border-dashed rounded p-6 relative flex flex-col justify-between h-80"
                             style={{ borderColor: `${accent}4d`, backgroundColor: `${accent}05` }}>
                            {leftTag && (
                                <div className="absolute -top-3 left-4 bg-zinc-950 px-2 py-0.5 rounded border border-zinc-800 text-[8px] font-mono"
                                     style={{ color: accent }}>
                                    {leftTag}
                                </div>
                            )}
                            <div className="space-y-4">
                                <span className="text-[10px] font-mono tracking-widest" style={{ color: accent }}>// CROWN TOWN HOTEL</span>
                                {leftTitle && <h3 className="text-2xl font-light text-white font-cormorant italic">{leftTitle}</h3>}
                                {leftDesc && <p className="text-zinc-400 text-[11px] leading-relaxed font-light">{leftDesc}</p>}
                            </div>
                            {leftFooter && (
                                <div className="text-[8px] font-mono text-zinc-500">
                                    {leftFooter}
                                </div>
                            )}
                        </div>

                        {/* Middle block representation */}
                        <div className="md:col-span-5 border border-dashed rounded p-6 relative flex flex-col justify-between h-80"
                             style={{ borderColor: `${accent}4d`, backgroundColor: `${accent}05` }}>
                            {centerTag && (
                                <div className="absolute -top-3 left-4 bg-zinc-950 px-2 py-0.5 rounded border border-zinc-800 text-[8px] font-mono"
                                     style={{ color: accent }}>
                                    {centerTag}
                                </div>
                            )}
                            <div className="space-y-4">
                                <div className="h-28 rounded bg-zinc-950 border border-zinc-850 flex items-center justify-center font-mono text-[9px] text-zinc-500 relative overflow-hidden">
                                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:16px_16px]" />
                                    {centerText}
                                </div>
                                <div className="flex justify-between items-center text-[10px] font-mono">
                                    <span className="text-zinc-400">{centerLabel}</span>
                                    <span style={{ color: accent }}>{centerValue}</span>
                                </div>
                            </div>
                            {centerFooter && (
                                <div className="text-[8px] font-mono text-zinc-500">
                                    {centerFooter}
                                </div>
                            )}
                        </div>

                        {/* Right block representation */}
                        <div className="md:col-span-3 border border-dashed rounded p-6 relative flex flex-col justify-between h-80"
                             style={{ borderColor: `${accent}4d`, backgroundColor: `${accent}05` }}>
                            {rightTag && (
                                <div className="absolute -top-3 left-4 bg-zinc-950 px-2 py-0.5 rounded border border-zinc-800 text-[8px] font-mono"
                                     style={{ color: accent }}>
                                    {rightTag}
                                </div>
                            )}
                            <div className="space-y-4">
                                <div className="p-3 bg-zinc-950 border border-zinc-850 rounded text-center">
                                    <span className="text-[10px] font-mono text-white block font-bold">{rightValue1}</span>
                                    <span className="text-[8px] font-mono text-zinc-500 block uppercase">{rightLabel1}</span>
                                </div>
                                <div className="p-3 bg-zinc-950 border border-zinc-850 rounded text-center">
                                    <span className="text-[10px] font-mono text-white block font-bold">{rightValue2}</span>
                                    <span className="text-[8px] font-mono text-zinc-500 block uppercase">{rightLabel2}</span>
                                </div>
                            </div>
                            {rightButtonText && (
                                <button className="w-full py-2 text-black font-mono text-[9px] font-bold rounded uppercase tracking-wider transition-opacity hover:opacity-90"
                                        style={{ backgroundColor: accent }}>
                                    {rightButtonText}
                                </button>
                            )}
                        </div>

                    </div>

                    {/* Extra Layout spec details underneath */}
                    <div className="mt-8 border-t border-zinc-900 pt-6 flex flex-wrap justify-between items-center text-[10px] font-mono text-zinc-500 gap-4">
                        {systemLabel && <div>{systemLabel}</div>}
                        {details && details.length > 0 && (
                            <div className="flex gap-4">
                                {details.map((item, idx) => (
                                    <span key={idx}>
                                        {item.label}: {item.value}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
